import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export interface UseRecentLabelOrderOptions {
  storageKey?: string
  max?: number
  disableReorder?: boolean
  initialSelectedIds?: string[]
}

export interface UseRecentLabelOrderResult<T> {
  orderedLabels: T[]
  applySelection: (selectedIds: string[]) => void
  recentIds: string[]
  resetRecent: () => void
}

const DEFAULT_STORAGE_KEY = 'recentLabelIdsOrder'
const DEFAULT_MAX = 50

function readOrder(key: string): string[] {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as string[]) : []
  } catch {
    return []
  }
}

function writeOrder(key: string, order: string[], max: number) {
  try {
    localStorage.setItem(key, JSON.stringify(order.slice(0, max)))
  } catch {
    // noop
  }
}

export function useRecentLabelOrder<T extends { id?: string | null }>(
  labels: T[] | undefined,
  options?: UseRecentLabelOrderOptions
): UseRecentLabelOrderResult<T> {
  const storageKey = options?.storageKey || DEFAULT_STORAGE_KEY
  const max = options?.max ?? DEFAULT_MAX
  const disableReorder = options?.disableReorder ?? false
  const initialSelectedIds = options?.initialSelectedIds || []

  const [recentIds, setRecentIds] = useState<string[]>(readOrder(storageKey))
  const prevSelectedRef = useRef<string[]>([...initialSelectedIds])

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === storageKey) setRecentIds(readOrder(storageKey))
    }
    window.addEventListener('storage', handler)
    return () => window.removeEventListener('storage', handler)
  }, [storageKey])

  const applySelection = useCallback(
    (selectedIds: string[]) => {
      const prev = prevSelectedRef.current

      const newlyAdded = selectedIds.filter(id => !prev.includes(id))
      prevSelectedRef.current = selectedIds
      if (disableReorder) return
      if (!newlyAdded.length) return
      setRecentIds(curr => {
        const cleaned = curr.filter(id => !newlyAdded.includes(id))
        for (let i = newlyAdded.length - 1; i >= 0; i--) {
          cleaned.unshift(newlyAdded[i])
        }
        writeOrder(storageKey, cleaned, max)
        return [...cleaned]
      })
    },
    [disableReorder, max, storageKey]
  )

  const resetRecent = useCallback(() => {
    try {
      localStorage.removeItem(storageKey)
    } catch {
      // noop
    }
    setRecentIds([])
  }, [storageKey])

  const orderedLabels = useMemo(() => {
    if (!labels || !labels.length) return [] as T[]
    const byId: Record<string, T> = {}
    labels.forEach(label => {
      const id = label?.id
      if (id) byId[id] = label
    })
    const existingRecent = recentIds.filter(id => byId[id])
    const remaining = labels.filter(label => {
      const id = label?.id
      return id && !existingRecent.includes(id)
    })
    return [...existingRecent.map(id => byId[id]), ...remaining]
  }, [labels, recentIds])

  return { orderedLabels, applySelection, recentIds, resetRecent }
}
